import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DrawerRegistryService } from '../../../shared/services/drawer-registry.service';

/**
 * Opciones para abrir el compose flotante de correo (tipo Gmail). Permite
 * prefill cross-módulo: cualquier listado/detalle de Contacto puede abrir el
 * compose con el destinatario ya cargado para que el usuario solo escriba.
 */
export interface EmailComposeOptions {
  /** Correo del destinatario (To). Si viene, se precarga como destinatario. */
  email?: string;
  /** Nombre legible del destinatario (para el chip). */
  fullName?: string;
  /** Id del contacto del CRM (link al timeline). */
  contactId?: number;
  /** Asunto pre-llenado. */
  subject?: string;
  /** Cuerpo (texto plano) pre-llenado. */
  body?: string;
  /**
   * Contexto de origen (Inc 24). Cuando el compose se abre desde el detalle de
   * un lead/deal/etc., estos campos hacen que el correo enviado quede
   * registrado en el timeline de esa entidad. Ej. { contextEntityType: 'LEAD',
   * contextEntityId: 42 }.
   */
  contextEntityType?: string;
  contextEntityId?: number;
}

export interface EmailComposeState {
  isOpen: boolean;
  options: EmailComposeOptions;
}

/**
 * Servicio GLOBAL del compose flotante de correo. Sigue el patrón de
 * TaskDrawerService: un BehaviorSubject de estado open/close + opciones de
 * prefill, e integra DrawerRegistryService (opened()/closed()) para coordinar
 * con el resto de overlays. El componente flotante (montado UNA vez en el
 * shell) se suscribe a `state$`.
 *
 * Apertura: header global, quick-actions de contacto, botón Redactar de la
 * bandeja, fila de la lista de contactos, etc. → `open(options?)`.
 * Tras enviar: `notifySent()` para que la bandeja recargue.
 */
@Injectable({ providedIn: 'root' })
export class EmailComposeService {
  private readonly registry = inject(DrawerRegistryService);
  private readonly stateSubject = new BehaviorSubject<EmailComposeState>({
    isOpen: false,
    options: {}
  });
  readonly state$ = this.stateSubject.asObservable();

  /** Emite cuando un correo se encoló con éxito (202) → la bandeja refresca. */
  private readonly sentSubject = new Subject<void>();
  readonly sent$ = this.sentSubject.asObservable();

  open(options: EmailComposeOptions = {}): void {
    if (!this.stateSubject.value.isOpen) this.registry.opened();
    this.stateSubject.next({ isOpen: true, options });
  }

  close(): void {
    if (this.stateSubject.value.isOpen) this.registry.closed();
    this.stateSubject.next({ isOpen: false, options: {} });
  }

  notifySent(): void {
    this.sentSubject.next();
  }
}
